import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, FileSignature, Upload, Monitor, CheckSquare, BookOpen, FormInput, Flame } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import SignatureModal from "@/components/SignatureModal";

const ICONS = { E_SIGNATURE: FileSignature, DOCUMENT_UPLOAD: Upload, FORM_INPUT: FormInput, IT_PROVISION: Monitor, POLICY_ACK: CheckSquare, TRAINING: BookOpen };

export default function MeHome() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [onb, setOnb] = useState(null);
  const [streak, setStreak] = useState(null);
  const [signTarget, setSignTarget] = useState(null);

  const load = async () => {
    const [d, o, s] = await Promise.all([
      api.get("/me/dashboard").catch(() => ({ data: null })),
      api.get("/me/onboarding").catch(() => ({ data: { onboarding: null } })),
      api.get("/me/streak").catch(() => ({ data: null })),
    ]);
    setDash(d.data);
    setOnb(o.data.onboarding);
    setStreak(s.data);
  };
  useEffect(() => { load(); }, []);

  const complete = async (taskId) => {
    try { await api.patch(`/me/onboarding/tasks/${taskId}`); toast.success("Task completed"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="me-home">
      {/* Hero next-milestone card */}
      <Card className="border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #00214D 0%, #001033 100%)" }} data-testid="me-hero-card">
        <CardContent className="p-8">
          <div className="text-[10px] uppercase tracking-widest text-pink font-semibold">Welcome back</div>
          <h1 className="text-3xl font-bold font-display mt-1">Hi {user?.name?.split(" ")[0] || "there"}, day 1 starts here.</h1>
          <p className="text-white/70 text-sm mt-2 max-w-xl">
            Complete your onboarding checklist below to get up and running. Your next milestone: {onb?.tasks?.find(t => t.status !== "COMPLETED")?.title || "You're all set!"}.
          </p>
          {onb && (
            <div className="mt-5 max-w-md">
              <div className="flex items-center justify-between mb-1 text-xs">
                <span className="text-white/80">Onboarding progress</span>
                <span className="font-mono font-bold" data-testid="me-progress-pct">{dash?.onboarding_progress?.percent ?? 0}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-pink transition-all" style={{ width: `${dash?.onboarding_progress?.percent ?? 0}%` }} />
              </div>
              <div className="text-[10px] text-white/60 mt-1">{dash?.onboarding_progress?.done ?? 0} of {dash?.onboarding_progress?.total ?? 0} tasks completed</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Habit-loop streak strip */}
      {streak && streak.streak_days > 0 && (
        <Card
          className="border-0 shadow-sm overflow-hidden"
          data-testid="me-streak-card"
        >
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink to-orange-400 grid place-items-center shrink-0">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-2xl font-bold font-display" data-testid="me-streak-days">{streak.streak_days}</span>
                <span className="text-sm text-neutral-600">day{streak.streak_days === 1 ? "" : "s"} in a row</span>
                {streak.streak_days >= 3 && <span className="text-xs px-2 py-0.5 rounded-full bg-pink/10 text-pink font-semibold">On fire</span>}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5" data-testid="me-streak-message">
                {streak.today_active
                  ? "Nice work today — you crushed something on your list."
                  : streak.streak_days === 1
                    ? "One task down. Keep the momentum by finishing one more today."
                    : `Don't break the chain — complete a task today to make it ${streak.streak_days + 1}.`}
              </div>
            </div>
            {/* Dot trail — last 7 days */}
            <div className="flex items-center gap-1.5" data-testid="me-streak-dots">
              {Array.from({ length: 7 }).map((_, i) => {
                const d = new Date();
                d.setDate(d.getDate() - (6 - i));
                const key = d.toISOString().slice(0, 10);
                const active = (streak.active_dates || []).includes(key);
                return (
                  <div
                    key={key}
                    title={d.toLocaleDateString()}
                    className={`w-3 h-3 rounded-full ${active ? "bg-pink" : "bg-neutral-200"}`}
                    data-testid={`me-streak-dot-${i}`}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-6">
          <div className="text-xs uppercase text-neutral-500">Onboarding</div>
          <div className="text-4xl font-bold text-pink mt-2">{dash?.onboarding_progress?.percent ?? 0}%</div>
          <div className="text-xs text-neutral-500 mt-1">{dash?.onboarding_progress?.done ?? 0} of {dash?.onboarding_progress?.total ?? 0} tasks</div>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-6">
          <div className="text-xs uppercase text-neutral-500">Annual Leave</div>
          <div className="text-4xl font-bold text-teal mt-2">{dash?.leave_balance?.annual ?? 0}</div>
          <div className="text-xs text-neutral-500 mt-1">days available</div>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-6">
          <div className="text-xs uppercase text-neutral-500">Sick Leave</div>
          <div className="text-4xl font-bold text-teal mt-2">{dash?.leave_balance?.sick ?? 0}</div>
          <div className="text-xs text-neutral-500 mt-1">days available</div>
        </CardContent></Card>
      </div>

      {/* Onboarding tasks */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-lg">Your onboarding tasks</h3>
            {onb && <span className="text-xs text-neutral-500">Started {new Date(onb.created_at).toLocaleDateString()}</span>}
          </div>
          {onb ? (
            <>
              <Progress value={dash?.onboarding_progress?.percent ?? 0} />
              <div className="mt-5 space-y-2">
                {onb.tasks.map(t => {
                  const Icon = ICONS[t.type] || Circle;
                  const isDone = t.status === "COMPLETED";
                  const isSig = t.type === "E_SIGNATURE";
                  return (
                    <div key={t.task_id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`me-task-${t.task_id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {isDone ? <CheckCircle2 className="w-4 h-4 text-teal shrink-0" /> : <Icon className="w-4 h-4 text-neutral-400 shrink-0" />}
                        <div>
                          <div className={`text-sm ${isDone ? "line-through text-neutral-400" : "font-medium"}`}>{t.title}</div>
                          <div className="text-xs text-neutral-500">{t.group} · Due {new Date(t.due_date).toLocaleDateString()}</div>
                        </div>
                      </div>
                      {!isDone && (
                        isSig
                          ? <Button size="sm" className="btn-primary rounded-full text-xs" onClick={() => setSignTarget({ onboardingId: onb.onboarding_id, task: t, defaultName: user?.name })} data-testid={`me-sign-${t.task_id}`}>Sign now</Button>
                          : <Button size="sm" variant="outline" className="text-xs" onClick={() => complete(t.task_id)} data-testid={`me-complete-${t.task_id}`}>Complete</Button>
                      )}
                      {isDone && t.signer_name && <span className="text-[10px] text-neutral-500">signed by {t.signer_name}</span>}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-neutral-500 text-center py-8">You have no active onboarding. If you've just been hired, refresh in a moment.</div>
          )}
        </CardContent>
      </Card>

      <SignatureModal
        open={!!signTarget}
        onOpenChange={(v) => !v && setSignTarget(null)}
        onboardingId={signTarget?.onboardingId}
        task={signTarget?.task}
        defaultName={signTarget?.defaultName || user?.name || ""}
        onSigned={() => load()}
      />
    </div>
  );
}
